"use client";
import React, { BaseSyntheticEvent, useState } from "react";
import styles from "./Footer.module.css";
import { multipleClassName, userAction } from "../../../utils/helpers";
import Image from "next/image";
import { emit, title } from "process";
import InputField from "../FormComponents/InputField/InputField";
import Button from "../Button/PrimaryButton";
import useIsMobile from "../../../customHooks/IsMobile";
import Modal from "../Modal/Modal";
import CustomImageComponent from "../Image/Image";
import { subscribeClick } from "../../../lib/analytics/AnalyticsEvents";

interface IlinkSection {
  label: string;
  link: string;
}

const LogoSection = ({
  title,
  content,
}: {
  title: string;
  content: React.ReactNode[];
}) => {
  return (
    <>
      <div className={styles.header}>{title}</div>
      <div className={styles.divider}></div>
      <div className={styles.contentContainerWithLogo}>
        {content.map((item: React.ReactNode) => {
          return item;
        })}
      </div>
    </>
  );
};

const LinkSection = ({
  title,
  content,
}: {
  title: string;
  content: IlinkSection[];
}) => {
  return (
    <>
      <div className={styles.header}>{title}</div>
      <div className={styles.divider}></div>
      <div className={styles.contentContainer}>
        {content.map((item: IlinkSection) => {
          return (
            <div style={{ display: "flex" }}>
              <a href={item.link} target="_blank" className={styles.link}>
                {item.label}
              </a>
            </div>
          );
        })}
      </div>
    </>
  );
};

const SubscribeSection = ({
  content,
}: {
  content: {
    title: string;
    label: string;
    buttonText: string;
    requiredError: string;
    invalidEmailError: string;
    loadingText: string;
    successMessage: string;
  };
}) => {
  const [email, setEmail] = useState("");
  const [error, setError] = useState<null | string>(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState("");
  const { isMobileDevice, windowWidth } = useIsMobile();

  const handleError = (e: BaseSyntheticEvent) => {
    const { value } = e.target;
    if (!value) {
      setError(content.requiredError);
    } else if (
      value.toString().match("^[a-zA-Z0-9_.±]+@[a-zA-Z0-9-]+.[a-zA-Z0-9-.]+$")
    ) {
      setError(null);
    } else {
      setError(content.invalidEmailError);
    }
    setEmail(value);
  };
  const handleSubmit = async () => {
    if (!email) {
      setError(content.requiredError);
    } else if (!error) {
      let result = {
        email: email,
      };
      try {
        setLoading(true);
        const res = await fetch("/api/handleSubscription", {
          method: "POST",
          body: JSON.stringify(result),
          headers: {
            "content-type": "application/json",
          },
        });
        let data = await res.json();
        if (res.ok) {
          setShowModal(true);
          setLoading(false);
          setResponse(content.successMessage);
        } else {
          setShowModal(true);
          setLoading(false);
          setResponse(content.successMessage);
        }
      } catch (error: any) {
        setLoading(false);
      }
    }
  };
  return (
    <>
      <div className={styles.subScribeTitle}>{content.title}</div>
      <div className={styles.emailFieldWrapper}>
        <Image
          src={
            "https://d3g4wmezrjkwkg.cloudfront.net/zo-business/staging/Images/email.svg"
          }
          alt="Email"
          height={36}
          width={36}
        />
        <input
          placeholder={content.label}
          name={"email"}
          onChange={handleError}
          value={email}
        />
      </div>
      {error && (
        <div
          className={styles.error}
          style={{
            visibility: error && error.length ? "visible" : "hidden",
          }}
        >
          <Image
            src={
              "https://d3g4wmezrjkwkg.cloudfront.net/zo-business/staging/Images/infoRed.webp"
            }
            width={16}
            height={16}
            alt="Error"
          />
          <div className={styles.errorText}>{error}</div>
        </div>
      )}
      <div className={styles.subscribeBtn}>
        <Button
          text={loading ? content.loadingText : content.buttonText}
          bgImage="linear-gradient(to bottom, #57cc03, #00630a), linear-gradient(to bottom, #a5f815 5%, #71d80b 101%)"
          gradientBorder="linear-gradient(to bottom, #a5f815 40%, #71d80b 100%)"
          overridingStyles={{
            fontSize: "clmap(16px,1.56,30px)",
            padding: "9px 94px",
            fontWeight: "700",
            fontStyle: "italic",
          }}
          onClick={() => userAction(subscribeClick, handleSubmit)}
        />
      </div>
      <div className={styles.subscribeBtnMobile}>
        <Button
          text={loading ? content.loadingText : content.buttonText}
          bgImage="linear-gradient(to bottom, #57cc03, #00630a), linear-gradient(to bottom, #a5f815 5%, #71d80b 101%)"
          gradientBorder="linear-gradient(to bottom, #a5f815 40%, #71d80b 100%)"
          containerStyle={{
            width: "100%",
          }}
          overridingStyles={{
            fontSize: "clmap(16px,1.56,30px)",
            height: "44px",
            fontWeight: "700",
            fontStyle: "italic",
            width: "100%",
          }}
          onClick={() => userAction(subscribeClick, handleSubmit)}
        />
      </div>
      {showModal && (
        <Modal>
          <div
            style={{
              width: isMobileDevice ? "90%" : "30%",
              height: "30%",
              backgroundColor: "#fff",
              position: "relative",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <div
              style={{
                position: "absolute",
                right: 10,
                top: 10,
                width: 20,
                height: 20,
                cursor: "pointer",
              }}
              onClick={() => setShowModal(false)}
            >
              <CustomImageComponent
                src={
                  "https://d3g4wmezrjkwkg.cloudfront.net/website/images/gdc/gdc-page-v2/crossButton.webp"
                }
                alt="Close"
                layout="fill"
              />
            </div>
            <p style={{ padding: "0px 20px" }}>{response}</p>
          </div>
        </Modal>
      )}
    </>
  );
};

const Footer = (props: any) => {
  const { isMobileDevice, windowWidth } = useIsMobile();
  const footerData = props.data;
  const logoSectionData = {
    socials: {
      content: footerData.socials.content.map(
        (object: { title: string; img: string; link: string; imgAlt: string }) => (
          <div
            style={{
              position: "relative",
              height: "clamp(36px,2.6vw,60px)",
              width: "clamp(36px,2.6vw,60px)",
            }}
          >
            <Image
              src={object.img}
              alt={object.imgAlt}
              style={{
                cursor: "pointer",
              }}
              onClick={() => {
                window.open(object.link, "_blank")!.focus();
              }}
              fill
              objectFit="contain"
            />
          </div>
        )
      ),
    },
    member: {
      content: footerData.member.content.map(
        (object: { title: string; img: string; imgAlt: string }) => (
          <div
            style={{
              position: "relative",
              height: "clamp(55px,2.6vw,60px)",
              width: "clamp(78px,4.47vw,86px)",
            }}
          >
            <Image
              src={object.img}
              alt={object.imgAlt}
              fill
              objectFit="contain"
            />
          </div>
        )
      ),
    },
    payment: {
      content: footerData.payment.content.map(
        (object: { title: string; img: string; imgAlt: string }) => (
          <div
            style={{
              position: "relative",
              height: "clamp(18px,1.25vw,24px)",
              width: "clamp(45px,4.6vw,90px)",
            }}
          >
            <Image
              src={object.img}
              alt={object.imgAlt}
              fill
              objectFit="contain"
            />
          </div>
        )
      ),
    },
  };
  return (
    <div className={styles.root}>
      <div className={styles.winzoImageMobileConatiner}>
        <div className={styles.winzoImageMobile}>
          <Image
            src="https://d3g4wmezrjkwkg.cloudfront.net/zo-business/staging/Images/winzoFooter.webp"
            alt="WINZO"
            fill
            objectFit="contain"
          />
        </div>
      </div>
      <div
        className={multipleClassName(
          "container",
          styles.footerContentContainer
        )}
      >
        <div className={styles.leftPanel}>
          <div className={styles.winzoImage}>
            <Image
              src="https://d3g4wmezrjkwkg.cloudfront.net/zo-business/staging/Images/winzoFooter.webp"
              alt="WINZO"
              fill
              objectFit="contain"
            />
          </div>
          <div className={styles.socials}>
            <LogoSection
              title={footerData.socials.title}
              content={logoSectionData.socials.content}
            />
          </div>
          <div className={styles.aboutWinzo}>
            <LinkSection
              title={footerData.aboutWinzo.title}
              content={footerData.aboutWinzo.content}
            />
          </div>
          <div className={styles.blogs}>
            <LinkSection
              title={footerData.blogs.title}
              content={footerData.blogs.content}
            />
          </div>
        </div>
        <div className={styles.rightPanel}>
          <div className={styles.subscribeArea}>
            <SubscribeSection content={footerData.subscribe} />
          </div>
          <div className={styles.socialsMobile}>
            <LogoSection
              title={footerData.socials.title}
              content={logoSectionData.socials.content}
            />
          </div>
          <div className={styles.memberOf}>
            <LogoSection
              title={footerData.member.title}
              content={logoSectionData.member.content}
            />
          </div>
          <div className={styles.paymentPartners}>
            <LogoSection
              title={footerData.payment.title}
              content={logoSectionData.payment.content}
            />
          </div>
        </div>
      </div>
      <div className={"container"}>
        <div className={styles.dividerMobile}></div>
        <div className={styles.footNote}>
          {`© ${new Date().getFullYear()}` + footerData.footNote}
        </div>
      </div>
    </div>
  );
};

export default Footer;
